// @file Padlet features content used for features page
import { n__, __ } from 'bits/intl'
import AppsForAnyDeviceImg from 'images/features/apps_for_any_device_img.png'
import BeautifulAndFunImg from 'images/features/beautiful_and_fun_img.png'
import EasyAndIntuitiveImg from 'images/features/easy_and_intuitive_img.png'
import ExcellentSupportImg from 'images/features/excellent_support_img.png'
import FlexibleAndVersatileImg from 'images/features/flexible_and_versatile_img.png'
import PerfectingCollaborationImg from 'images/features/perfecting_collaboration_img.png'
import PersonalProfileImg from 'images/features/personal_profile_img.png'
import PremiumPlansImg from 'images/features/premium_plans_img.png'
import PrivateAndSecureImg from 'images/features/private_and_secure_img.png'
import SearchForInformationImg from 'images/features/search_for_information_img.png'
import SupportEveryFileTypeImg from 'images/features/support_every_file_type_img.png'
import TakeItWithYouImg from 'images/features/take_it_with_you_img.png'
import UniversalAndInclusiveImg from 'images/features/universal_and_inclusive_img.png'

interface Feature {
  featureName: string
  previewImageUrl: string
  featureTitle: string
  featureSubtitle: string
  featureHighlights: string[]
  appStoreIcons?: Record<string, any>
}

interface FeaturesCollection {
  [key: string]: Feature
}

const linkToPremium = '<a href="/memberships">' + __('Learn more at %{url}', { url: 'padlet.com/memberships' }) + '</a>'
const features: FeaturesCollection = {
  feature: {
    featureName: 'Easy and intuitive',
    previewImageUrl: EasyAndIntuitiveImg,
    featureTitle: __('Easy and intuitive'),
    featureSubtitle: __(
      'Even if you’ve never used any kind of productivity software before, Padlet is familiar and fun.',
    ),
    featureHighlights: [
      __('Add posts with one click, copy-paste, or drag and drop'),
      __('Works the way your mind works - with sight, sound, and touch'),
      __('Changes are autosaved'),
      __('Simple link sharing allows for quick collaboration'),
    ],
  },
  feature2: {
    featureName: 'Universal and inclusive',
    previewImageUrl: UniversalAndInclusiveImg,
    featureTitle: __('Universal and inclusive'),
    featureSubtitle: __('We’ll meet you where you are.'),
    featureHighlights: [
      n__(
        'Available in %{num} language, with more being added',
        'Available in %{num} languages, with more being added',
        42,
        { num: 42 },
      ),
      __('Collaborate on padlets from around the globe'),
      __('Working towards greater accessibility every day'),
    ],
  },
  feature3: {
    featureName: 'Perfecting collaboration',
    previewImageUrl: PerfectingCollaborationImg,
    featureTitle: __('Perfecting the art of collaboration'),
    featureSubtitle: __(
      'Include your best friends, family, and colleagues, so no one will grumble about being left out.',
    ),
    featureHighlights: [
      __('Invite others to contribute - signup not required'),
      __('Work with unlimited contributors'),
      __('Give read-only, writing, moderator, or admin access; revoke at any time'),
      __('Watch updates appear instantly across devices'),
      __('Allow others to remake your work to use as a template'),
    ],
  },
  feature4: {
    featureName: 'Support every file type',
    previewImageUrl: SupportEveryFileTypeImg,
    featureTitle: __('Supports (almost) every file type'),
    featureSubtitle: __('From spreadsheets to selfies to Spotify, we’ll take care of your favorite links and uploads.'),
    featureHighlights: [
      __('Upload files from your computer, take a picture or video from your phone, or link from the web'),
      __('Post images, documents, videos, music, and files from Photoshop, Illustrator, Autocad, and more'),
      __(
        'Embed content from anywhere on the web, including YouTube, Instagram, Twitter, Vimeo, The New York Times, and 400 other apps',
      ),
      __(
        'Attached links and files will appear with a gorgeous contextual preview to give viewers a hint of what you’re showing them',
      ),
    ],
  },
  feature5: {
    featureName: 'Apps for any device',
    previewImageUrl: AppsForAnyDeviceImg,
    featureTitle: __('Apps for (most) any device'),
    featureSubtitle: __('Don’t miss any of the action, even when you’re away from a computer.'),
    featureHighlights: [
      __('Available on iOS (iPhone, iPad, iPod Touch), Android, and Kindle devices'),
      __('Posts appear instantly across devices'),
      __('Take photos and scan QR codes from within each app'),
      __('Stellar app ratings (see what we did there?)'),
    ],
    appStoreIcons: {
      ios: {
        url: 'https://padlet.com/ios-app',
        img: 'https://padlet.net/app-stores/itunes/logos/app_store_en.svg',
        title: 'iOS icon',
      },
      android: {
        url: 'https://padlet.com/android-app',
        img: 'https://padlet.net/app-stores/google_play/logos/app_store_en.png',
        title: 'Android icon',
      },
      kindle: {
        url: 'https://padlet.com/i-kindle-app',
        img: 'https://padlet.net/app-stores/amazon/logos/app_store_en.svg',
        title: 'Kindle icon',
      },
    },
  },
  feature6: {
    featureName: 'Beautiful and fun',
    previewImageUrl: BeautifulAndFunImg,
    featureTitle: __('Beautiful and fun'),
    featureSubtitle: __('Make content as beautiful as you are. No design experience necessary.'),
    featureHighlights: [
      __('Beautiful attachment previews (rather than boring links) show off the content you want to share'),
      __('Colorful wallpaper choices range from cute animations to professional wood, cork, or chalkboard'),
      __('Pick a theme that fits your writing sensibility, from comic-book to typewriter'),
      __('Gorgeous typography to bring out the blogger in you'),
    ],
  },
  feature7: {
    featureName: 'Private and secure',
    previewImageUrl: PrivateAndSecureImg,
    featureTitle: __('Private and secure'),
    featureSubtitle: __('Publish your best work, and protect your innermost thoughts.'),
    featureHighlights: [
      __('Choose from five options: Public, Secret, Password Protected, Totally Private, and Organization-Wide'),
      __('From each option, choose who can view, write on, or moderate your padlets'),
      __('Premium users enjoy an organization-wide internal network separate from the rest of Padlet'),
      __('Publish your work to the world when you are ready'),
    ],
  },
  feature8: {
    featureName: 'Flexible and versatile',
    previewImageUrl: FlexibleAndVersatileImg,
    featureTitle: __('Flexible and versatile'),
    featureSubtitle: __('Whether blogging, bookmarking, or bolstering discussion, no need to look elsewhere.'),
    featureHighlights: [
      __(
        '3 vastly different layouts that convey information as a square board, a feed of information, or as an open canvas',
      ),
      __('Utilize wallpapers as organization tools, from a Venn Diagram, to KWL chart, to a calendar, to a To-Do list'),
      __('Use as a publishing tool or private notes app, thanks to comprehensive privacy options'),
      __('Supports minimalists and maximalists alike'),
      __('From sticky notes to essays, encompasses all types of writing'),
    ],
  },
  feature9: {
    featureName: 'Take it with you',
    previewImageUrl: TakeItWithYouImg,
    featureTitle: __('Take it with you'),
    featureSubtitle: __('Integrate Padlet with other apps, or go old-school with paper. We’ll even doggy bag it.'),
    featureHighlights: [
      __('Choose a custom link address that’s easy to remember, then just copy and paste!'),
      __('Export as PDF, CSV, Image, or Excel File'),
      __('Gorgeous social previews for Twitter, Facebook, Slack, and Padlet itself'),
      __('Embed padlets on your website or blog'),
    ],
  },
  feature10: {
    featureName: 'Premium plans',
    previewImageUrl: PremiumPlansImg,
    featureTitle: __('Premium plans'),
    featureSubtitle: __('Make the most of Padlet for your school or company.'),
    featureHighlights: [
      __('Create a private network separate from “vanilla” Padlet'),
      __('Manage users and monitor activity'),
      __('Enjoy more wallpapers and themes'),
      __('Store bigger files'),
      __('Create a custom domain'),
      linkToPremium,
    ],
  },
  feature11: {
    featureName: 'Personal profile',
    previewImageUrl: PersonalProfileImg,
    featureTitle: __('Personal profile'),
    featureSubtitle: __('Less embarrassing than Facebook, less buttoned-up than LinkedIn.'),
    featureHighlights: [
      __('A public feed of your padlets'),
      __('Use it as a portfolio of your best creations'),
      __('Let your collaborators and followers know a bit more about you'),
      __('Participate in the community by following users with common interests'),
      __('Personalize your profile with custom wallpapers and colors'),
    ],
  },
  feature12: {
    featureName: 'Search for information',
    previewImageUrl: SearchForInformationImg,
    featureTitle: __('Search for information and inspiration'),
    featureSubtitle: __('Internal search that knows what you’re digging for.'),
    featureHighlights: [
      __('Find your padlets and padlets you’ve contributed to from your dashboard'),
      __('Search for padlets on specific topics'),
      __('Start from a gallery of premade templates or remake any padlet you admire to use as your own'),
      __('Connect with other Padlet users via community features'),
    ],
  },
  feature13: {
    featureName: 'Excellent support',
    previewImageUrl: ExcellentSupportImg,
    featureTitle: __('Excellent support'),
    featureSubtitle: __('We’ll be there for you, when the rain starts to fall.'),
    featureHighlights: [
      __('Personalized assistance that goes above and beyond the norm'),
      __('Round the clock support available'),
      __(
        "Reach out via email to <a href='mailto:hello@padlet.com'>%{email}</a> or on <a target='_blank' href='https://twitter.com/padlet'>Twitter</a> or <a target='_blank' href='https://facebook.com/padlet'>Facebook</a>",
        { email: 'hello@padlet.com' },
      ),
      __('User forums for all of your burning questions'),
    ],
  },
}

export { features }
